import { useState } from 'react';
import { useProfile } from '@/api/profile';
import { useMyEnrollments, useEnrollmentCounts } from '@/api/enrollments';
import { useMyReadings } from '@/api/documents';
import { useUnreadCount } from '@/api/notifications';
import { useCategoryLabel } from '@/api/categories';
import { PositionFilter } from '@/components/shared/position-filter';
import { LoadingSpinner } from '@/components/shared/loading-spinner';
import { ErrorMessage } from '@/components/shared/error-message';
import { Link } from 'react-router';
import { GraduationCap, FileText, Award, Bell, BookOpen, Shield } from 'lucide-react';
import { cn } from '@/lib/utils';
import { primaryCompanyName } from '@/lib/formatters';
import { useActiveCompany } from '@/context/active-company-context';
import { WelcomeCard } from './_components/welcome-card';
import { ActiveCoursesSection } from './_components/active-courses-section';
import { PendingReadingsSection } from './_components/pending-readings-section';

export function DashboardPage() {
  const [positionId, setPositionId] = useState<number | undefined>(undefined);
  const categoryLabel = useCategoryLabel('course_category');
  const documentCategoryLabel = useCategoryLabel('document_category');
  const { data: profile, isLoading: profileLoading, error: profileError } = useProfile();
  const { activeCompany } = useActiveCompany();
  // Multiempresa #15: show the ACTIVE company (matches the header switcher) so the card
  // never contradicts the company the portal is currently scoped to; fall back to the
  // primary membership when no active company is resolved yet.
  const companyName = activeCompany?.name ?? primaryCompanyName(profile?.user_companies);
  const { data: enrollmentsRes } = useMyEnrollments({ status: 'active', position_id: positionId });
  const { data: unreadCount } = useUnreadCount();
  const { data: counts } = useEnrollmentCounts(positionId);
  const { data: readingsRes } = useMyReadings({ status: 'pending', per_page: 5 });

  if (profileLoading) return <LoadingSpinner />;
  if (profileError) return <ErrorMessage message="No se pudo cargar el perfil." />;

  const activeEnrollments = enrollmentsRes?.data ?? [];

  // Progreso general
  const totalCourses = counts
    ? (counts.active ?? 0) + (counts.completed ?? 0) + (counts.expired ?? 0)
    : 0;
  const completedCourses = counts?.completed ?? 0;
  const progressPercent =
    totalCourses > 0 ? Math.round((completedCourses / totalCourses) * 100) : 0;

  const statCards = [
    {
      label: 'Cursos Activos',
      value: activeEnrollments.length,
      icon: GraduationCap,
      href: '/courses',
      iconBg: 'bg-blue-100 text-blue-600',
    },
    {
      label: 'Notificaciones',
      value: `${unreadCount ?? 0} sin leer`,
      icon: Bell,
      href: '/notifications',
      iconBg: 'bg-amber-100 text-amber-600',
    },
    {
      label: 'Certificados',
      value: 'Ver todos',
      icon: Award,
      href: '/certificates',
      iconBg: 'bg-green-100 text-green-600',
    },
    {
      label: 'Competencia',
      value: 'Ver estado',
      icon: Shield,
      href: '/competency',
      iconBg: 'bg-purple-100 text-purple-600',
    },
  ];

  return (
    <div className="space-y-6">
      <WelcomeCard profile={profile} companyName={companyName} />

      <PositionFilter value={positionId} onChange={setPositionId} />

      {/* Stats row */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {statCards.map((card) => (
          <Link
            key={card.href}
            to={card.href}
            className="group rounded-xl border border-border bg-card p-4 transition-shadow hover:shadow-md"
          >
            <div className="flex items-center gap-3">
              <div className={cn('rounded-lg p-2.5', card.iconBg)}>
                <card.icon className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-muted-foreground">{card.label}</p>
                <p className="truncate text-lg font-semibold text-card-foreground">{card.value}</p>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Overall training progress */}
      {totalCourses > 0 && (
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-foreground">Mi Progreso General</h2>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {completedCourses} de {totalCourses} cursos completados
              </p>
            </div>
            <span className="text-2xl font-bold text-primary">{progressPercent}%</span>
          </div>
          <div className="mt-3 h-3 w-full overflow-hidden rounded-full bg-muted">
            <div
              className={cn(
                'h-full rounded-full transition-all',
                progressPercent >= 100 ? 'bg-green-500' : 'bg-primary',
              )}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <div className="mt-2 flex gap-4 text-xs text-muted-foreground">
            <span>
              Activos: <strong className="text-foreground">{counts?.active ?? 0}</strong>
            </span>
            <span>
              Completados: <strong className="text-green-600">{completedCourses}</strong>
            </span>
            {(counts?.expired ?? 0) > 0 && (
              <span>
                Vencidos: <strong className="text-red-600">{counts?.expired ?? 0}</strong>
              </span>
            )}
          </div>
        </div>
      )}

      <ActiveCoursesSection activeEnrollments={activeEnrollments} categoryLabel={categoryLabel} />

      <PendingReadingsSection
        readings={readingsRes?.data ?? []}
        categoryLabel={documentCategoryLabel}
      />

      {/* Quick links */}
      <section>
        <h2 className="mb-4 text-lg font-semibold text-foreground">Acceso Rápido</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { label: 'Documentos', icon: FileText, href: '/documents' },
            { label: 'Mis Cursos', icon: BookOpen, href: '/courses' },
            { label: 'Certificados', icon: Award, href: '/certificates' },
            { label: 'Competencia', icon: Shield, href: '/competency' },
          ].map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className="flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-3 text-sm font-medium text-card-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
            >
              <link.icon className="h-4 w-4 text-muted-foreground" />
              {link.label}
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
